import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Receipt, Printer, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import InvoicePrint from '../../components/InvoicePrint';
import AxiosDB from '@/config/axios2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    filterPayments();
  }, [searchTerm, statusFilter, payments]);

  const fetchPayments = async () => {
    try {
      const response = await AxiosDB.get(`/payments/search`);
      setPayments(response.data);
      setFilteredPayments(response.data);
    } catch (error) {
      toast.error('Error al cargar el historial de pagos');
    } finally {
      setLoading(false);
    }
  };

  const filterPayments = () => {
    let filtered = [...payments];

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.payment_method.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    setFilteredPayments(filtered);
  };

  const getTotalAmount = () => {
    return filteredPayments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="payment-history-page">
      <div>
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Historial de Pagos
        </h2>
        <p className="text-gray-600">Todas tus transacciones en un solo lugar</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="shadow-lg" data-testid="total-payments-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-gray-600">Total de Pagos</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {filteredPayments.length}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-lg" data-testid="completed-payments-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-gray-600">Completados</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {filteredPayments.filter(p => p.status === 'completed').length}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-lg" data-testid="total-amount-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-gray-600">Total Gastado</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-purple-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              ${getTotalAmount()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por descripción o método de pago..."
                data-testid="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="w-full sm:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="completed">Completados</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="failed">Fallidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Payments List */}
      <Card className="shadow-lg" data-testid="payments-list">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Transacciones</CardTitle>
          <CardDescription>{filteredPayments.length} pagos encontrados</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredPayments.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-xl font-semibold text-gray-500">No se encontraron pagos</p>
              <p className="text-gray-400 mt-2">Intenta cambiar los filtros de búsqueda</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPayments.map((payment) => (
                <div
                  key={payment.id}
                  data-testid={`payment-item-${payment.id}`}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        <Receipt className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold">{payment.description}</p>
                        <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-500">
                          <span>
                            {new Date(payment.date).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                          <span>•</span>
                          <span>{payment.payment_method}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {payment.divisa} {Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(payment?.amount || 0)}
                      </p>
                      <Badge
                        className={
                          payment.status === 'Pagado'
                            ? 'bg-green-100 text-green-800 hover:bg-green-100'
                            : payment.status === 'failed'
                              ? 'bg-red-100 text-red-800 hover:bg-red-100'
                              : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100'
                        }
                      >
                        {payment.status}
                      </Badge>
                    </div>
                    {payment.invoice_data && payment.status === 'Pagado' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`print-invoice-${payment.id}`}
                        style={{ width: 40 }}
                        onClick={() => {
                          setSelectedPayment(payment);
                          setShowInvoice(true);
                        }}
                      >
                        <Printer className="h-4 w-4 mr-2" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`print-invoice-${payment.id}`}
                        disabled
                        style={{ width: 40 }}
                      >

                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Dialog */}
      <Dialog open={showInvoice} onOpenChange={setShowInvoice}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Factura</DialogTitle>
          </DialogHeader>
          {selectedPayment && <InvoicePrint payment={selectedPayment} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentHistory;